import {formatReferenceCopy} from 'utils/typography';
import CommissionHelp from '../../../commission-help';
import {COLORS} from 'config/constants';
import React from "react";
import {useTranslation} from "react-i18next";
import { Controller, useForm} from "react-hook-form";
import {useDispatch} from "react-redux";
import isEmpty from "lodash/isEmpty";

import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { Check as CheckIcon } from '@vedmarket/ui-kit';

import {checkDecimalsAfterDot} from "../../../../../../../../../../../../../utils/validation";
import { ADD_PAYMENT_FORM, AddParamsPaymentForm } from "../../../../index";
import {ADORNMENTS} from "../../../../../../../../../../../../../config/constants";
import MenuButton from "../../../../../../../../../../../../common/menuButton";
import {
  useFormattedDecimalInput,
  useGetCalcIdFromRoute,
} from "../../../../../../../../../../../../../utils/hooks";

import {
  useUpdateBankCommissionsByInvoiceId,
} from "../../../../../../../../../../../../../api/calc";

import './payment-item-edit.scss';

type Props = {
    index: number
    invoiceAmount: number
    invoiceNumber: string
    isFirstItem?: boolean
    data: BankCommissions
    remove(id: number): void
    onClose(): void
}

const PaymentItemEdit = ({
  index,
  isFirstItem,
  invoiceAmount,
  invoiceNumber,
  remove,
  data,
  onClose
}: Props) => {

  const { t } = useTranslation();

  const copy = (key: string) => formatReferenceCopy(t(key));

  const dispatch = useDispatch()

  const orderId = useGetCalcIdFromRoute()

  const decimalInput = useFormattedDecimalInput();

  const methods = useForm<AddParamsPaymentForm>({
    defaultValues: {
      [ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE]: data?.commission_percentage ?? '',
      [ADD_PAYMENT_FORM.PAYMENT_AMOUNT]: data?.payment_amount ?? '',
      [ADD_PAYMENT_FORM.BANK_TRANSFER_FEE]: data?.bank_transfer_fee ?? '',
      [ADD_PAYMENT_FORM.MIN_BANK_TRANSFER_FEE]: data?.min_bank_transfer_fee ?? '',
      [ADD_PAYMENT_FORM.MAX_BANK_TRANSFER_FEE]: data?.max_bank_transfer_fee ?? '',
      [ADD_PAYMENT_FORM.CURRENCY_CONTROL_FEE]: data?.currency_control_fee ?? '',
      [ADD_PAYMENT_FORM.MIN_CURRENCY_CONTROL_FEE]: data?.min_currency_control_fee ?? '',
      [ADD_PAYMENT_FORM.MAX_CURRENCY_CONTROL_FEE]: data?.max_currency_control_fee ?? '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    setError,
    clearErrors,
    setValue,
    trigger,
    formState: {errors},
    control
  } = methods;

  const errorNumber = copy('errors__message__incorrectValue');

  const onSubmit = handleSubmit (async (values) => {

    if (!values){
      return
    }

    const paymentData = {
      ...values,
      id: data?.id,
      index,
    }

    const dataToSend = {
      orderId,
      invoiceNumber,
      data: paymentData
    }

    // @ts-ignore
    dispatch(useUpdateBankCommissionsByInvoiceId(dataToSend));

    onClose();

  })

  const handleChangeCommissionPercentage = (rawValue: string) => {
    const value = Number(rawValue);
    const isErrorValue = value < 0 || value > 100;

    if (isErrorValue || (checkDecimalsAfterDot(value, errorNumber) !== true)){
      setError(ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE, { type: "custom",  message: copy('errors__message__incorrectValue') });
    } else {
      clearErrors([ ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE ])
      const amount = (invoiceAmount * value) / 100;
      setValue(
        ADD_PAYMENT_FORM.PAYMENT_AMOUNT,
        amount.toFixed(2),
        { shouldValidate: true }
      );
      trigger(ADD_PAYMENT_FORM.PAYMENT_AMOUNT);
    }

  }

  const handleChangePaymentAmount = (rawValue: string) => {
    const value = Number(rawValue);
    const isErrorValue = value < 0 || value > invoiceAmount;

    if (isErrorValue || checkDecimalsAfterDot(value, errorNumber) !== true){
      setError(ADD_PAYMENT_FORM.PAYMENT_AMOUNT, { type: "custom",  message: copy('errors__message__incorrectValue') });
    } else {
      clearErrors([ ADD_PAYMENT_FORM.PAYMENT_AMOUNT ])
      const percentage = (value / invoiceAmount * 100).toFixed(2);
      setValue(ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE, percentage);
      trigger(ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE);
    }

  }

  const menuOptions = [
    {
      title: copy('actions__text__delete'),
      onClick: () => remove(index),
    }
  ];

  return <form className='payment-item-edit'>
    <div className='payment-item-edit__menuButtons'>
      <MenuButton options={menuOptions}/>
    </div>
    <Typography variant='subtitle1' sx={{
      color: COLORS.BLACK_DARK,
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: '24px',
      letterSpacing: 0,
      paddingRight: '48px',
    }}>{copy('calc__text__bankCommissions__invoicePayment')}</Typography>
    <div className='payment-item-edit__form-row'>
      <Controller
        control={control}
        name={ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE}
        rules={{
          required: true,
          validate: (value) => checkDecimalsAfterDot(value, errorNumber),
          min: { value: 0, message: errorNumber },
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
              handleChangeCommissionPercentage(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE);
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={`${copy('calc__text__bankCommissions__payment')}, ${copy('calc__text__bankCommissions__percentages')}`}
            fullWidth
            InputLabelProps={{ shrink: true }}
            InputProps={{ endAdornment: ADORNMENTS.PERCENT }}
          />
        )}
      />
      <Controller
        control={control}
        name={ADD_PAYMENT_FORM.PAYMENT_AMOUNT}
        rules={{
          required: true,
          validate: (value) => checkDecimalsAfterDot(value, errorNumber),
          min: { value: 0, message: errorNumber },
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: ADD_PAYMENT_FORM.PAYMENT_AMOUNT,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(ADD_PAYMENT_FORM.PAYMENT_AMOUNT);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
              handleChangePaymentAmount(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(ADD_PAYMENT_FORM.PAYMENT_AMOUNT);
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={`${copy('calc__text__bankCommissions__payment')}, ${copy('calc__text__bankCommissions__amount')}`}
            fullWidth
            InputLabelProps={{ shrink: true }}
            InputProps={{ endAdornment: ADORNMENTS.PRICE }}
          />
        )}
      />
    </div>
    <Typography variant='subtitle1' sx={{
      color: COLORS.BLACK_DARK,
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: '24px',
      letterSpacing: 0,
      marginBottom: '8px',
    }}>{copy('calc__text__bankCommissions__bankCommissionForTheTransfer')}</Typography>
    <CommissionHelp />
    <div className='payment-item-edit__form-row'>
      <Controller
        control={control}
        name={ADD_PAYMENT_FORM.BANK_TRANSFER_FEE}
        rules={{
          required: true,
          validate: (value) => checkDecimalsAfterDot(value, errorNumber),
          min: { value: 0, message: errorNumber },
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: ADD_PAYMENT_FORM.BANK_TRANSFER_FEE,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(ADD_PAYMENT_FORM.BANK_TRANSFER_FEE);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(ADD_PAYMENT_FORM.BANK_TRANSFER_FEE);
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={copy('calc__text__bankCommissions__commission')}
            fullWidth
            InputLabelProps={{ shrink: true }}
            InputProps={{ endAdornment: ADORNMENTS.PERCENT }}
          />
        )}
      />
      <Controller
        control={control}
        name={ADD_PAYMENT_FORM.MIN_BANK_TRANSFER_FEE}
        rules={{
          validate: (value) => value ? checkDecimalsAfterDot(value, errorNumber) : true,
          min: { value: 0, message: errorNumber },
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: ADD_PAYMENT_FORM.MIN_BANK_TRANSFER_FEE,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(ADD_PAYMENT_FORM.MIN_BANK_TRANSFER_FEE);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(ADD_PAYMENT_FORM.MIN_BANK_TRANSFER_FEE);
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={copy('calc__text__bankCommissions__minCommission')}
            fullWidth
            InputLabelProps={{ shrink: true }}
            InputProps={{ endAdornment: ADORNMENTS.PRICE }}
          />
        )}
      />
      <Controller
        name={ADD_PAYMENT_FORM.MAX_BANK_TRANSFER_FEE}
        rules={{
          validate: (value) => value ? checkDecimalsAfterDot(value, errorNumber) : true,
          min: { value: 0, message: errorNumber },
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: ADD_PAYMENT_FORM.MAX_BANK_TRANSFER_FEE,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(ADD_PAYMENT_FORM.MAX_BANK_TRANSFER_FEE);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(ADD_PAYMENT_FORM.MAX_BANK_TRANSFER_FEE);
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={copy('calc__text__bankCommissions__maxCommission')}
            fullWidth
            InputLabelProps={{ shrink: true }}
            InputProps={{ endAdornment: ADORNMENTS.PRICE }}
          />
        )}
      />
    </div>
    <Typography variant='subtitle1' sx={{
      color: COLORS.BLACK_DARK,
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: '24px',
      letterSpacing: 0,
      marginBottom: '8px',
    }}>{copy('calc__text__bankCommissions__currencyControlCommission')}</Typography>
    <CommissionHelp isCurrencyControl />
    <div className='payment-item-edit__form-row'>
      <Controller
        control={control}
        name={ADD_PAYMENT_FORM.CURRENCY_CONTROL_FEE}
        rules={{
          required: true,
          validate: (value) => checkDecimalsAfterDot(value, errorNumber),
          min: { value: 0, message: errorNumber },
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: ADD_PAYMENT_FORM.CURRENCY_CONTROL_FEE,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(ADD_PAYMENT_FORM.CURRENCY_CONTROL_FEE);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(ADD_PAYMENT_FORM.CURRENCY_CONTROL_FEE);
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={copy('calc__text__bankCommissions__commission')}
            fullWidth
            InputLabelProps={{ shrink: true }}
            InputProps={{ endAdornment: ADORNMENTS.PERCENT }}
          />
        )}
      />

      <Controller
        control={control}
        name={ADD_PAYMENT_FORM.MIN_CURRENCY_CONTROL_FEE}
        rules={{
          validate: (value) => value ? checkDecimalsAfterDot(value, errorNumber) : true,
          min: { value: 0, message: errorNumber },
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: ADD_PAYMENT_FORM.MIN_CURRENCY_CONTROL_FEE,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(ADD_PAYMENT_FORM.MIN_CURRENCY_CONTROL_FEE);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(ADD_PAYMENT_FORM.MIN_CURRENCY_CONTROL_FEE);
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={copy('calc__text__bankCommissions__minCommission')}
            fullWidth
            InputLabelProps={{ shrink: true }}
            InputProps={{ endAdornment: ADORNMENTS.PRICE }}
          />
        )}
      />
      <Controller
        control={control}
        name={ADD_PAYMENT_FORM.MAX_CURRENCY_CONTROL_FEE}
        rules={{
          validate: (value) => value ? checkDecimalsAfterDot(value, errorNumber) : true,
          min: { value: 0, message: errorNumber },
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: ADD_PAYMENT_FORM.MAX_CURRENCY_CONTROL_FEE,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(ADD_PAYMENT_FORM.MAX_CURRENCY_CONTROL_FEE);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(ADD_PAYMENT_FORM.MAX_CURRENCY_CONTROL_FEE);
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={copy('calc__text__bankCommissions__maxCommission')}
            fullWidth
            InputLabelProps={{ shrink: true }}
            InputProps={{ endAdornment: ADORNMENTS.PRICE }}
          />
        )}
      />
    </div>
    <div className='payment-item-edit__buttons'>
      <Button
        variant='outlined'
        fullWidth
        onClick={onClose}
      >{copy('actions__text__cancel')}</Button>
      <Button
        variant='contained'
        fullWidth
        startIcon={<CheckIcon />}
        disabled={!isEmpty(errors)}
        onClick={onSubmit}
      >{copy('actions__text__save')}</Button>
    </div>
  </form>
}

export default PaymentItemEdit
